import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../custom-markdown";
import { AiHomeworkIdeaCardProps } from "./AiHomeworkIdeaCard";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

// Helper to get complexity level colors for PDF
const getComplexityPdfStyle = (level: string) => {
  // Check for exact match first, then partial includes to be more precise
  if (level === "BEGINNER") {
    return {
      backgroundColor: "#dcfce7",
      color: "#166534",
      borderColor: "#22c55e",
      titleColor: "#166534",
    };
  } else if (level === "INTERMEDIATE") {
    return {
      backgroundColor: "#fef3c7",
      color: "#92400e",
      borderColor: "#f59e0b",
      titleColor: "#92400e",
    };
  } else if (level === "ADVANCED") {
    return {
      backgroundColor: "#fee2e2",
      color: "#b91c1c",
      borderColor: "#ef4444",
      titleColor: "#b91c1c",
    };
  }

  // Default teal colors
  return {
    backgroundColor: "#ccfbf1",
    color: "#0f766e",
    borderColor: "#14b8a6",
    titleColor: "#0f766e",
  };
};

const styles = StyleSheet.create({
  ideaContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#f0fdf4",
    borderRadius: 8,
  },
  ideaTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 8,
  },
  complexityBadge: {
    alignSelf: "flex-start",
    padding: "4 8",
    borderRadius: 4,
    fontSize: 9,
    marginBottom: 8,
  },
  timeBadge: {
    alignSelf: "flex-start",
    padding: "4 8",
    borderRadius: 4,
    fontSize: 9,
    backgroundColor: "#f3e8ff",
    color: "#6b21a8",
    marginBottom: 8,
    marginLeft: 4,
  },
  badgeContainer: {
    flexDirection: "row",
    marginBottom: 6,
  },
  description: {
    fontSize: 10,
    color: "#374151",
    lineHeight: 1.6,
    marginBottom: 10,
  },
  resourceItem: {
    flexDirection: "row",
    marginBottom: 6,
    paddingLeft: 8,
  },
  bullet: {
    width: 12,
    fontSize: 11,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginTop: 12,
    marginBottom: 8,
  },
  subSectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginTop: 8,
    marginBottom: 6,
  },
  sectionContainer: {
    borderLeftWidth: 4,
    paddingLeft: 8,
    marginBottom: 12,
  },
  specialSection: {
    backgroundColor: "#e6f7ff33",
    padding: 8,
    borderRadius: 4,
    marginBottom: 12,
    borderLeftWidth: 4,
  },
  teacherSection: {
    backgroundColor: "#e6fff733",
    padding: 8,
    borderRadius: 4,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: "#14b8a6",
  },
  studentSection: {
    backgroundColor: "#e6f0ff33",
    padding: 8,
    borderRadius: 4,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: "#3b82f6",
  },
  criteriaContainer: {
    flexDirection: "row",
    marginBottom: 8,
    alignItems: "flex-start",
  },
  criteriaIcon: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: "#bfdbfe",
    color: "#1d4ed8",
    textAlign: "center",
    fontSize: 8,
    marginRight: 6,
    marginTop: 2,
  },
  teacherIcon: {
    backgroundColor: "#99f6e4",
    color: "#0f766e",
  },
});

const AiHomeworkIdeaDocument = ({
  homeworkIdea,
  customizationSettings,
}: AiHomeworkIdeaCardProps) => {
  const { t } = useTranslation();

  // Handle backward compatibility - use reportIdeas if available, otherwise fall back to ideas
  const ideas = homeworkIdea.reportIdeas || homeworkIdea.ideas || [];

  // Get complexity style for the first idea to use for header color
  const firstIdeaComplexity = ideas[0]?.complexity_level;
  const headerColor = firstIdeaComplexity
    ? getComplexityPdfStyle(firstIdeaComplexity).titleColor
    : "#16a34a";

  return (
    <PdfLayout
      courseName={homeworkIdea.subject}
      gradeLevel={homeworkIdea.gradeLevel}
      topic={homeworkIdea.title || homeworkIdea.topic}
      imageUrl={homeworkIdea.imageUrl}
      headerTextColor={headerColor}
      customizationSettings={customizationSettings}
      createdDate={homeworkIdea.createdAt}
      author={homeworkIdea.authorName}
    >
      {ideas.map((idea, index) => {
        // Try to get complexity from the field or from the translation
        const translatedComplexity = t(
          `aiHomeworkIdeas.complexity.${idea.complexity_level}`,
        );
        // Get complexity style for this idea
        const complexityStyle = getComplexityPdfStyle(idea.complexity_level);

        return (
          <View
            key={index}
            style={[
              styles.ideaContainer,
              { backgroundColor: complexityStyle.backgroundColor + "33" },
            ]}
            break={index > 0}
          >
            <MarkdownRenderer
              customStyle={{
                text: {
                  text: {
                    ...styles.ideaTitle,
                    fontSize: scaleFontSize(
                      styles.ideaTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                    color: complexityStyle.titleColor,
                  },
                  container: {
                    marginBottom: 8,
                  },
                },
              }}
              content={idea.title}
            />

            {/* Badges container */}
            <View style={styles.badgeContainer}>
              <Text
                style={[
                  styles.complexityBadge,
                  {
                    backgroundColor: complexityStyle.backgroundColor,
                    color: complexityStyle.color,
                    fontSize: scaleFontSize(
                      styles.complexityBadge.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {translatedComplexity}
              </Text>

              {/* Estimated time badge */}
              {idea.estimated_time && (
                <Text
                  style={[
                    styles.timeBadge,
                    {
                      fontSize: scaleFontSize(
                        styles.timeBadge.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {idea.estimated_time}
                </Text>
              )}
            </View>

            {/* Assignment Description Section */}
            <Text
              style={[
                styles.sectionTitle,
                {
                  fontSize: scaleFontSize(
                    styles.sectionTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                  color: complexityStyle.titleColor,
                },
              ]}
            >
              {t("aiHomeworkIdeas.assignmentDescription")}
            </Text>
            <View
              style={[
                styles.sectionContainer,
                { borderLeftColor: complexityStyle.borderColor },
              ]}
            >
              <MarkdownRenderer
                customStyle={{
                  text: {
                    text: {
                      ...styles.description,
                      fontSize: scaleFontSize(
                        styles.description.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                    container: {
                      marginBottom: 12,
                      paddingVertical: 2,
                    },
                  },
                }}
                content={idea.description}
              />
            </View>

            {/* Why AI-Resistant Section */}
            <View break>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.sectionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                    color: complexityStyle.titleColor,
                  },
                ]}
              >
                {t("aiHomeworkIdeas.whyAiResistant")}
              </Text>
              <View
                style={[
                  styles.sectionContainer,
                  { borderLeftColor: complexityStyle.borderColor },
                ]}
              >
                <MarkdownRenderer
                  customStyle={{
                    text: {
                      text: {
                        ...styles.description,
                        fontSize: scaleFontSize(
                          styles.description.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                      container: {
                        marginBottom: 12,
                        paddingVertical: 2,
                      },
                    },
                    listItem: {
                      text: {
                        ...styles.description,
                        fontSize: scaleFontSize(
                          styles.description.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                      container: {
                        marginBottom: 12,
                        paddingVertical: 2,
                      },
                    },
                  }}
                  content={idea.explanation}
                />
              </View>
            </View>

            {/* Purpose Alignment Section */}
            {idea.purpose_alignment && (
              <View break>
                <Text
                  style={[
                    styles.sectionTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.sectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                      color: complexityStyle.titleColor,
                    },
                  ]}
                >
                  {t("aiHomeworkIdeas.purposeAlignment")}
                </Text>
                <View
                  style={[
                    styles.sectionContainer,
                    { borderLeftColor: "#3b82f6" },
                  ]}
                >
                  <MarkdownRenderer
                    customStyle={{
                      text: {
                        text: {
                          ...styles.description,
                          fontSize: scaleFontSize(
                            styles.description.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                        container: {
                          marginBottom: 12,
                          paddingVertical: 2,
                        },
                      },
                    }}
                    content={idea.purpose_alignment}
                  />
                </View>
              </View>
            )}

            {/* Outline Section */}
            {idea.outline && (
              <View break>
                <Text
                  style={[
                    styles.sectionTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.sectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                      color: complexityStyle.titleColor,
                    },
                  ]}
                >
                  {t("aiHomeworkIdeas.outline")}
                </Text>
                <View
                  style={[
                    styles.sectionContainer,
                    { borderLeftColor: "#6b7280" },
                  ]}
                >
                  <MarkdownRenderer
                    customStyle={{
                      text: {
                        text: {
                          ...commonStyles.sectionContent,
                          fontSize: scaleFontSize(
                            commonStyles.sectionContent.fontSize,
                            customizationSettings?.fontSize,
                          ),
                          color: "#374151",
                        },
                        container: {
                          ...commonStyles.sectionContent,
                          marginBottom: 12,
                          paddingVertical: 2,
                        },
                      },
                    }}
                    content={idea.outline}
                  />
                </View>
              </View>
            )}

            {/* Resources Section */}
            {(idea.resources || idea.recommended_resources) && (
              <View break>
                <Text
                  style={[
                    styles.sectionTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.sectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                      color: complexityStyle.titleColor,
                    },
                  ]}
                >
                  {t("aiHomeworkIdeas.resources")}
                </Text>

                {/* Modern resources structure */}
                {idea.resources && (
                  <View
                    style={[
                      styles.sectionContainer,
                      { borderLeftColor: "#6b7280" },
                    ]}
                  >
                    {/* Digital Resources */}
                    {idea.resources.digital_resources &&
                      idea.resources.digital_resources.length > 0 && (
                        <View style={{ marginBottom: 10 }}>
                          <Text
                            style={[
                              styles.subSectionTitle,
                              {
                                fontSize: scaleFontSize(
                                  styles.subSectionTitle.fontSize,
                                  customizationSettings?.fontSize,
                                ),
                                color: "#4b5563",
                              },
                            ]}
                          >
                            {t("aiHomeworkIdeas.digitalResources")}
                          </Text>
                          {idea.resources.digital_resources.map(
                            (resource, idx) => (
                              <View key={idx} style={styles.resourceItem}>
                                <Text
                                  style={[styles.bullet, { color: "#4b5563" }]}
                                >
                                  •
                                </Text>
                                <Text
                                  style={[
                                    styles.description,
                                    {
                                      fontSize: scaleFontSize(
                                        styles.description.fontSize,
                                        customizationSettings?.fontSize,
                                      ),
                                    },
                                  ]}
                                >
                                  {resource}
                                </Text>
                              </View>
                            ),
                          )}
                        </View>
                      )}

                    {/* Printed Materials */}
                    {idea.resources.printed_materials &&
                      idea.resources.printed_materials.length > 0 && (
                        <View style={{ marginBottom: 10 }}>
                          <Text
                            style={[
                              styles.subSectionTitle,
                              {
                                fontSize: scaleFontSize(
                                  styles.subSectionTitle.fontSize,
                                  customizationSettings?.fontSize,
                                ),
                                color: "#4b5563",
                              },
                            ]}
                          >
                            {t("aiHomeworkIdeas.printedMaterials")}
                          </Text>
                          {idea.resources.printed_materials.map(
                            (resource, idx) => (
                              <View key={idx} style={styles.resourceItem}>
                                <Text
                                  style={[styles.bullet, { color: "#4b5563" }]}
                                >
                                  •
                                </Text>
                                <Text
                                  style={[
                                    styles.description,
                                    {
                                      fontSize: scaleFontSize(
                                        styles.description.fontSize,
                                        customizationSettings?.fontSize,
                                      ),
                                    },
                                  ]}
                                >
                                  {resource}
                                </Text>
                              </View>
                            ),
                          )}
                        </View>
                      )}

                    {/* Tools and Materials */}
                    {idea.resources.tools_and_materials &&
                      idea.resources.tools_and_materials.length > 0 && (
                        <View style={{ marginBottom: 10 }}>
                          <Text
                            style={[
                              styles.subSectionTitle,
                              {
                                fontSize: scaleFontSize(
                                  styles.subSectionTitle.fontSize,
                                  customizationSettings?.fontSize,
                                ),
                                color: "#4b5563",
                              },
                            ]}
                          >
                            {t("aiHomeworkIdeas.toolsAndMaterials")}
                          </Text>
                          {idea.resources.tools_and_materials.map(
                            (resource, idx) => (
                              <View key={idx} style={styles.resourceItem}>
                                <Text
                                  style={[styles.bullet, { color: "#4b5563" }]}
                                >
                                  •
                                </Text>
                                <Text
                                  style={[
                                    styles.description,
                                    {
                                      fontSize: scaleFontSize(
                                        styles.description.fontSize,
                                        customizationSettings?.fontSize,
                                      ),
                                    },
                                  ]}
                                >
                                  {resource}
                                </Text>
                              </View>
                            ),
                          )}
                        </View>
                      )}
                  </View>
                )}

                {/* Legacy recommended resources */}
                {idea.recommended_resources &&
                  idea.recommended_resources.length > 0 && (
                    <View
                      style={[
                        styles.sectionContainer,
                        { borderLeftColor: "#6b7280" },
                      ]}
                    >
                      {idea.recommended_resources.map((resource, idx) => (
                        <View key={idx} style={styles.resourceItem}>
                          <Text
                            style={[
                              styles.bullet,
                              { color: complexityStyle.titleColor },
                            ]}
                          >
                            {idx + 1}.
                          </Text>
                          <MarkdownRenderer
                            customStyle={{
                              text: {
                                text: {
                                  ...styles.description,
                                  fontSize: scaleFontSize(
                                    styles.description.fontSize,
                                    customizationSettings?.fontSize,
                                  ),
                                },
                                container: {
                                  marginBottom: 0,
                                },
                              },
                              listItem: {
                                text: {
                                  ...styles.description,
                                  fontSize: scaleFontSize(
                                    styles.description.fontSize,
                                    customizationSettings?.fontSize,
                                  ),
                                },
                                container: {
                                  marginBottom: 0,
                                },
                              },
                            }}
                            content={resource}
                          />
                        </View>
                      ))}
                    </View>
                  )}
              </View>
            )}

            {/* Teacher Guidelines Section */}
            {idea.teacher_guidelines &&
              ((idea.teacher_guidelines.feedback_tips ?? []).length > 0 ||
                (idea.teacher_guidelines.grading_criteria ?? []).length > 0 ||
                idea.teacher_guidelines.time_estimation) && (
                <View break>
                  <Text
                    style={[
                      styles.sectionTitle,
                      {
                        fontSize: scaleFontSize(
                          styles.sectionTitle.fontSize,
                          customizationSettings?.fontSize,
                        ),
                        color: complexityStyle.titleColor,
                      },
                    ]}
                  >
                    {t("aiHomeworkIdeas.teacherGuidelines")}
                  </Text>
                  <View style={styles.teacherSection}>
                    {/* Feedback Tips */}
                    {idea.teacher_guidelines.feedback_tips &&
                      idea.teacher_guidelines.feedback_tips.length > 0 && (
                        <View style={{ marginBottom: 10 }}>
                          <Text
                            style={[
                              styles.subSectionTitle,
                              {
                                fontSize: scaleFontSize(
                                  styles.subSectionTitle.fontSize,
                                  customizationSettings?.fontSize,
                                ),
                                color: "#0f766e",
                              },
                            ]}
                          >
                            {t("aiHomeworkIdeas.feedbackTips")}
                          </Text>
                          {idea.teacher_guidelines.feedback_tips.map(
                            (tip, idx) => (
                              <View key={idx} style={styles.criteriaContainer}>
                                <Text
                                  style={[
                                    styles.criteriaIcon,
                                    styles.teacherIcon,
                                  ]}
                                >
                                  {idx + 1}
                                </Text>
                                <Text
                                  style={[
                                    styles.description,
                                    {
                                      fontSize: scaleFontSize(
                                        styles.description.fontSize,
                                        customizationSettings?.fontSize,
                                      ),
                                      flex: 1,
                                    },
                                  ]}
                                >
                                  {tip}
                                </Text>
                              </View>
                            ),
                          )}
                        </View>
                      )}

                    {/* Grading Criteria */}
                    {idea.teacher_guidelines.grading_criteria &&
                      idea.teacher_guidelines.grading_criteria.length > 0 && (
                        <View style={{ marginBottom: 10 }}>
                          <Text
                            style={[
                              styles.subSectionTitle,
                              {
                                fontSize: scaleFontSize(
                                  styles.subSectionTitle.fontSize,
                                  customizationSettings?.fontSize,
                                ),
                                color: "#0f766e",
                              },
                            ]}
                          >
                            {t("aiHomeworkIdeas.gradingCriteria")}
                          </Text>
                          {idea.teacher_guidelines.grading_criteria.map(
                            (criteria, idx) => (
                              <View key={idx} style={styles.criteriaContainer}>
                                <Text
                                  style={[
                                    styles.criteriaIcon,
                                    styles.teacherIcon,
                                  ]}
                                >
                                  {idx + 1}
                                </Text>
                                <Text
                                  style={[
                                    styles.description,
                                    {
                                      fontSize: scaleFontSize(
                                        styles.description.fontSize,
                                        customizationSettings?.fontSize,
                                      ),
                                      flex: 1,
                                    },
                                  ]}
                                >
                                  {criteria}
                                </Text>
                              </View>
                            ),
                          )}
                        </View>
                      )}

                    {/* Time Estimation */}
                    {idea.teacher_guidelines.time_estimation && (
                      <View style={{ marginBottom: 10 }}>
                        <Text
                          style={[
                            styles.subSectionTitle,
                            {
                              fontSize: scaleFontSize(
                                styles.subSectionTitle.fontSize,
                                customizationSettings?.fontSize,
                              ),
                              color: "#0f766e",
                            },
                          ]}
                        >
                          {t("aiHomeworkIdeas.timeEstimation")}
                        </Text>
                        <Text
                          style={[
                            styles.description,
                            {
                              fontSize: scaleFontSize(
                                styles.description.fontSize,
                                customizationSettings?.fontSize,
                              ),
                              marginLeft: 8,
                            },
                          ]}
                        >
                          {idea.teacher_guidelines.time_estimation}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              )}

            {/* Student Criteria Section */}
            {idea.student_criteria &&
              ((idea.student_criteria.self_assessment_questions ?? []).length >
                0 ||
                (idea.student_criteria.success_indicators ?? []).length >
                  0) && (
                <View break={true}>
                  <Text
                    style={[
                      styles.sectionTitle,
                      {
                        fontSize: scaleFontSize(
                          styles.sectionTitle.fontSize,
                          customizationSettings?.fontSize,
                        ),
                        color: complexityStyle.titleColor,
                      },
                    ]}
                  >
                    {t("aiHomeworkIdeas.studentCriteria")}
                  </Text>
                  <View style={styles.studentSection}>
                    {/* Self Assessment Questions */}
                    {idea.student_criteria.self_assessment_questions &&
                      idea.student_criteria.self_assessment_questions.length >
                        0 && (
                        <View style={{ marginBottom: 10 }}>
                          <Text
                            style={[
                              styles.subSectionTitle,
                              {
                                fontSize: scaleFontSize(
                                  styles.subSectionTitle.fontSize,
                                  customizationSettings?.fontSize,
                                ),
                                color: "#1e40af",
                              },
                            ]}
                          >
                            {t("aiHomeworkIdeas.selfAssessmentQuestions")}
                          </Text>
                          {idea.student_criteria.self_assessment_questions.map(
                            (question, idx) => (
                              <View key={idx} style={styles.criteriaContainer}>
                                <Text style={styles.criteriaIcon}>
                                  {idx + 1}
                                </Text>
                                <Text
                                  style={[
                                    styles.description,
                                    {
                                      fontSize: scaleFontSize(
                                        styles.description.fontSize,
                                        customizationSettings?.fontSize,
                                      ),
                                      flex: 1,
                                    },
                                  ]}
                                >
                                  {question}
                                </Text>
                              </View>
                            ),
                          )}
                        </View>
                      )}

                    {/* Success Indicators */}
                    {idea.student_criteria.success_indicators &&
                      idea.student_criteria.success_indicators.length > 0 && (
                        <View style={{ marginBottom: 10 }}>
                          <Text
                            style={[
                              styles.subSectionTitle,
                              {
                                fontSize: scaleFontSize(
                                  styles.subSectionTitle.fontSize,
                                  customizationSettings?.fontSize,
                                ),
                                color: "#1e40af",
                              },
                            ]}
                          >
                            {t("aiHomeworkIdeas.successIndicators")}
                          </Text>
                          {idea.student_criteria.success_indicators.map(
                            (indicator, idx) => (
                              <View key={idx} style={styles.criteriaContainer}>
                                <Text style={styles.criteriaIcon}>✓</Text>
                                <Text
                                  style={[
                                    styles.description,
                                    {
                                      fontSize: scaleFontSize(
                                        styles.description.fontSize,
                                        customizationSettings?.fontSize,
                                      ),
                                      flex: 1,
                                    },
                                  ]}
                                >
                                  {indicator}
                                </Text>
                              </View>
                            ),
                          )}
                        </View>
                      )}
                  </View>
                </View>
              )}
          </View>
        );
      })}
    </PdfLayout>
  );
};

function AiHomeworkIdeaCardPDF(props: AiHomeworkIdeaCardProps) {
  return <AiHomeworkIdeaDocument {...props} />;
}

export default AiHomeworkIdeaCardPDF;
